import { CIActionBtnGroup, CIActionGuard, CIStatusCapsule, CITableGrid, Checkbox, getLocaleDateTimeFormat, toLocalDate, useUserPermission } from 'ccs-ui-components';
import { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import { requiredAuthPermission } from '../../../common/constants/UserPermissionConstants';
import { ApiModifySort, JourneyListState, Labels, ListJourneysProps } from '../../modules/journey-list/Utility';

import DropdownMenuButton from '../../../common/components/UI/menu/DropdownMenuButton';
import { STATUS_CLASS_TYPES } from '../imported-journey-summary/utility/StatusConstant';
import classes from './ListJourneys.module.scss';
import { REGISTRY_TYPE } from '../../../dashboard/constants/CommonConstants';
import { journeyBuilderRoutes } from '../../constants/JdRoutes';

const ListJourneys = ({
  statusSort,
  setStatusSort,
  lastModifiedSort,
  setLastModifiedSort,
  updatedBySort,
  setUpdatedBySort,
  sortedName,
  setSortedName,
  journeyNameSort,
  setJourneyNameSort,
  selectedJourneyList,
  handleClick,
  sortedOrder,
  setSortedOrder,
  setSortParameter,
  queryParams,
  setQueryParams,
  handleSelectAll,
  isCheckAllJourneys,
  footerObj,
  journeyList,
  viewJourney,
  editJourney,
  onDeleteSuccess
}: ListJourneysProps) => {
  // hooks
  const { t: translate } = useTranslation();
  const { permissionObject } = useUserPermission();

  /**
   * @method getJourneyDescription
   * @description extracts description from journeyStatusHistory for PENDING_APPROVAL status
   * @param journeyStatusHistory
   * @returns description or "No description provided"
   */
  const getJourneyDescription = (journeyStatusHistory?: JourneyListState['journeyStatusHistory']): string => {
    if (!journeyStatusHistory || journeyStatusHistory.length === 0) {
      return String(translate('journey_builder_component.msg_no_description_provided'));
    }

    const pendingApprovalEntry = journeyStatusHistory.find(
      (entry) => entry.status?.toUpperCase() === 'PENDING_APPROVAL'
    );

    if (!pendingApprovalEntry) {
      return String(translate('journey_builder_component.msg_no_description_provided'));
    }

    if (pendingApprovalEntry.description && pendingApprovalEntry.description.trim()) {
      return pendingApprovalEntry.description.trim();
    }

    return String(translate('journey_builder_component.msg_no_description_provided'));
  };

  /**
   * @method renderJourneyName
   * @description renders the journey name and journey version
   * @param row
   * @returns {journey name and journey version}
   */
  const renderJourneyName = (row: JourneyListState) => {
    const journeyUrl = journeyBuilderRoutes.journeyCanvas
      .replace(':mode', 'view')
      .replace(':id', row.journeyName)
      .replace(':version', row.version.toString());

    const description = getJourneyDescription(row.journeyStatusHistory);
    const tooltipText = `${row.journeyName}\n${description}`;

    return (
      <div className={classes['render-journey-name-body']} data-testid="journeyName">
        <Checkbox
          type="checkbox"
          onChange={(e) => handleClick(e, row.version)}
          id={row.journeyName}
          checked={
            selectedJourneyList.some((item) => item.recordName === row.journeyName && item.recordVersion === row.version)
            && row.status.toLocaleLowerCase() === Labels.JOURNEY_STATUS_ACTIVE
          }
          disabled={row.status.toLocaleLowerCase() !== Labels.JOURNEY_STATUS_ACTIVE}
          data-testid="selectCheckBox"
        />
        <div className={classes['journey-name-ver-alignment']}>
          <p className={classes['journey-name']} title={tooltipText}>
            <a
              href={journeyUrl}
              className={classes['journey-name-link']}
              onClick={(e) => {
                // Prevent default only for regular left-clicks (allows Ctrl+click, Cmd+click for new tab)
                // Right-clicks are handled by onContextMenu, which we don't prevent, so context menu shows
                if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
                  e.preventDefault();
                  viewJourney(row.journeyName, row.version.toString());
                }
              }}
            >
              {row.journeyName}
            </a>
          </p>
          <p className={classes.version}>
            {translate('journey_list_component.txt_version', {
              replace: {
                version: row.version
              }
            })}
          </p>
        </div>
      </div>
    );
  };

  /**
   * @method renderJourneyGroupName
   * @description renders the group name
   * @param row
   * @returns group name
   */
  const renderJourneyGroupName = (row: JourneyListState) => (
    <div className={classes['last-modified-data']} title={row.journeyCategory}>{row.journeyCategory}</div>
  );

  /**
   * @method renderJourneyStatus
   * @description renders the journey status
   * @param row
   * @returns status
   */
  const renderJourneyStatus = (row: JourneyListState) => (
    <CIStatusCapsule status={row.status} comment={row.journeyStatusHistory?.find((item) => item.status.toLocaleLowerCase() === Labels.JOURNEY_STATUS_RETURNED)?.comments} />
  );

  /**
   * @method renderLastModified
   * @description renders the journey last modified date
   * @param row
   * @returns last modified date
   */
  const renderLastModified = (row: JourneyListState) => {
    const date = toLocalDate({ date: row.updatedTimestamp });
    return (
      <div className={classes['last-modified-data']} title={date}>{date}</div>
    );
  };

  /**
   * @method createdBy
   * @description renders the name of the person who created or updated the journey
   * @param row
   * @returns updatedBy
   */
  const createdBy = (row: JourneyListState) => (
    <div className={classes['last-modified-data']} title={row.updatedBy}>{row.updatedBy}</div>
  );

  /**
   * @method renderActionBtn
   * @description renders the action buttons for journey list
   * @param row
   * @returns {view button and edit button}
   */
  const renderActionBtn = (row: JourneyListState) => {
    const actionButtons: {
      label: string;
      key: 'editIcon' | 'deleteIcon' | 'viewIcon' | 'addFillIcon' | 'addIcon' | 'refreshIcon' | 'bellIcon' | 'rightArrowIcon' | 'leftArrowIcon' | 'xMarkLargeIcon' | 'xMarkSmallIcon' | 'bottonArrowIcon' | 'upArrowIcon' | 'caretDownIcon' | 'caretUpIcon' | 'caretLeftIcon' | 'caretRightIcon' | 'caretDownFillIcon' | 'caretUpFillIcon' | 'caretLeftFillIcon' | 'caretRightFillIcon' | 'infoIcon' | 'infoFillIcon' | 'chatTextIcon' | 'chatTextFillIcon' | 'exclamationIcon' | 'exclamationFillIcon' | 'checkCircleIcon' | 'checkCircleFillIcon' | 'sortAscIcon' | 'sortDescIcon' | 'sortDisableIcon' | 'flowDiagramIcon' | 'hamBurgerIcon' | 'directionRightIcon' | 'uploadIcon' | 'xCircleIcon' | 'copyIcon' | 'largeCheckIcon' | 'checkIcon' | 'thinCheckIcon';
      icon?: React.ReactElement;
      onClick?: MouseEventHandler<HTMLButtonElement>;
      disabled?: boolean;
      className?: string;
      variant?: string;
      testId?: string;
    }[] = [{
      label: 'view',
      key: 'viewIcon',
      onClick: () => viewJourney(row.journeyName, row.version.toString()),
    }];

    if (CIActionGuard([requiredAuthPermission.JD.approve, requiredAuthPermission.JD.edit], permissionObject.permission)) {
      actionButtons.push({
        label: 'edit',
        key: 'editIcon',
        onClick: () => { editJourney(row.journeyName, row.status, row.version.toString()); },
        disabled: row.status.toLocaleLowerCase() === Labels.JOURNEY_STATUS_PENDING_APPROVAL
      });
    }
    return (
      <div className={classes['action-col']}>
        <CIActionBtnGroup
          buttons={actionButtons}
        />
        <DropdownMenuButton
          status={row.status}
          deleteURL={`journeys/${row.journeyName}/version/${row.version}`}
          onDeleteSuccess={onDeleteSuccess}
          showOn="list"
          registryType={REGISTRY_TYPE.JOURNEY}
          registryName={row.journeyName}
          entityData={row}
        />
      </div>
    );
  };

  /**
  * @method journeyNameSortToggleHandler
  * @description sorts the list by journey name (Ascending or Descending)
  */
  const journeyNameSortToggleHandler = () => {
    setStatusSort(undefined);
    setLastModifiedSort('');
    setUpdatedBySort(undefined);
    setSortedName(Labels.JOURNEY_NAME);
    if (journeyNameSort === ApiModifySort.ASC) {
      setSortedOrder(ApiModifySort.DESC);
      setJourneyNameSort(ApiModifySort.DESC);
      setSortParameter(`${Labels.JOURNEY_NAME},DESC`);
      setQueryParams((pre) => ({
        ...pre,
        sort: Labels.JOURNEY_NAME,
        type: ApiModifySort.DESC
      }));
    } else {
      setJourneyNameSort(ApiModifySort.ASC);
      setSortedOrder(ApiModifySort.ASC);
      setSortParameter(`${Labels.JOURNEY_NAME},ASC`);
      setQueryParams((pre: { [key: string]: string }) => ({
        ...pre,
        sort: Labels.JOURNEY_NAME,
        type: ApiModifySort.ASC
      }));
    }
  };

  /**
  * @method statusSortToggleHandler
  * @description sorts the list by status (Ascending or Descending)
  */
  const statusSortToggleHandler = () => {
    setJourneyNameSort(undefined);
    setLastModifiedSort('');
    setUpdatedBySort(undefined);
    setSortedName(Labels.JOURNEY_STATUS);
    if (statusSort === ApiModifySort.ASC) {
      setStatusSort(ApiModifySort.DESC);
      setSortedOrder(ApiModifySort.DESC);
      setSortParameter(`${Labels.JOURNEY_STATUS},DESC`);
      setQueryParams((pre) => ({
        ...pre,
        sort: Labels.JOURNEY_STATUS,
        type: ApiModifySort.DESC
      }));
    } else {
      setStatusSort(ApiModifySort.ASC);
      setSortedOrder(ApiModifySort.ASC);
      setSortParameter(`${Labels.JOURNEY_STATUS},ASC`);
      setQueryParams((pre) => ({
        ...pre,
        sort: Labels.JOURNEY_STATUS,
        type: ApiModifySort.ASC
      }));
    }
  };

  /**
   * @method lastModifiedSortToggleHandler
   * @description sorts the list by last modified (Ascending or Descending)
   */
  const lastModifiedSortToggleHandler = () => {
    setStatusSort(undefined);
    setJourneyNameSort(undefined);
    setUpdatedBySort(undefined);
    setSortedName(Labels.LAST_MODIFIED);
    if (lastModifiedSort === ApiModifySort.ASC) {
      setLastModifiedSort(ApiModifySort.DESC);
      setSortedOrder(ApiModifySort.DESC);
      setSortParameter(`${Labels.LAST_MODIFIED},DESC`);
      setQueryParams((pre) => ({
        ...pre,
        sort: Labels.LAST_MODIFIED,
        type: ApiModifySort.DESC
      }));
    } else {
      setLastModifiedSort(ApiModifySort.ASC);
      setSortedOrder(ApiModifySort.ASC);
      setSortParameter(`${Labels.LAST_MODIFIED},ASC`);
      setQueryParams((pre) => ({
        ...pre,
        sort: Labels.LAST_MODIFIED,
        type: ApiModifySort.ASC
      }));
    }
  };

  /**
   * @method updatedBySortToggleHandler
   * @description sorts the list by updated by (Ascending or Descending)
   */
  const updatedBySortToggleHandler = () => {
    setStatusSort(undefined);
    setLastModifiedSort('');
    setJourneyNameSort(undefined);
    setSortedName(Labels.UPDATED_BY);
    if (updatedBySort === ApiModifySort.ASC) {
      setUpdatedBySort(ApiModifySort.DESC);
      setSortedOrder(ApiModifySort.DESC);
      setSortParameter(`${Labels.UPDATED_BY},DESC`);
      setQueryParams((pre) => ({
        ...pre,
        sort: Labels.UPDATED_BY,
        type: ApiModifySort.DESC
      }));
    } else {
      setUpdatedBySort(ApiModifySort.ASC);
      setSortedOrder(ApiModifySort.ASC);
      setSortParameter(`${Labels.UPDATED_BY},ASC`);
      setQueryParams((pre) => ({
        ...pre,
        sort: Labels.UPDATED_BY,
        type: ApiModifySort.ASC
      }));
    }
  };

  return (
    <CITableGrid
      className={classes['journey-list-table-data']}
      headers={[
        {
          name: Labels.JOURNEY_NAME,
          label: (
            <div className={classes['journey-name-checkbox']}>
              <Checkbox
                type="checkbox"
                id="selectAll"
                onChange={handleSelectAll}
                checked={isCheckAllJourneys}
                disabled={journeyList.length > 10 || journeyList.some((item) => item?.status?.toLocaleLowerCase() !== STATUS_CLASS_TYPES.SUCCESS)}
                data-testid="selectAllCheckBox"
              />
              <div className={classes['render-journey-name']}>{translate('journey_list_component.lbl_journey_name')}</div>
            </div>),
          options: { breakPointWidth: 3 },
          columnData: {
            field: Labels.JOURNEY_NAME, render: renderJourneyName
          },
          sortOptions: {
            callback: journeyNameSortToggleHandler
          },
          headerClassName: classes['check-box-input']
        },
        {
          name: Labels.GROUP_NAME,
          label: translate('journey_list_component.lbl_group_name'),
          options: { breakPointWidth: 2 },
          columnData: {
            field: Labels.GROUP_NAME, render: renderJourneyGroupName
          }
        },
        {
          name: Labels.JOURNEY_STATUS,
          label: translate('journey_list_component.lbl_status'),
          sortOptions: {
            callback: statusSortToggleHandler
          },
          options: { breakPointWidth: 2 },
          columnData: {
            field: Labels.JOURNEY_STATUS, render: renderJourneyStatus
          }
        },
        {
          name: Labels.LAST_MODIFIED,
          label: translate('journey_list_component.lbl_last_modified'),
          labelOptions: { helperText: `(${getLocaleDateTimeFormat()})`, helperTextClass: classes['last-modified-header'] },
          sortOptions: {
            callback: lastModifiedSortToggleHandler
          },
          options: { breakPointWidth: 2 },
          columnData: {
            field: Labels.LAST_MODIFIED, render: renderLastModified
          }
        },
        {
          name: Labels.UPDATED_BY,
          label: translate('journey_list_component.lbl_modified_by'),
          sortOptions: {
            callback: updatedBySortToggleHandler
          },
          options: { breakPointWidth: 2 },
          columnData: {
            field: Labels.UPDATED_BY, render: createdBy
          }
        },
        {
          name: Labels.ACTION_BTN,
          label: '',
          options: { breakPointWidth: 1 },
          columnData: {
            field: Labels.ACTION_BTN, render: renderActionBtn, className: classes['button-alignment']
          }
        }
      ]}
      footer={footerObj}
      sorted={
        { sortedName: queryParams.sort ?? sortedName, sortedOrder: queryParams.type ?? sortedOrder }
      }
      tableData={journeyList}
    />
  );
};

export default ListJourneys;
